import { faMagnifyingGlass, faSpinner, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import DialpadButton from '@/components/DialpadButton';
import ProfileMenu from '@/components/ProfileMenu';
import SidebarNavRail from '@/components/SidebarNavRail';
import { WORKSPACE_SIDEBAR_LEFT_CLASS, WORKSPACE_SIDEBAR_Z_CLASS } from '@/constants/layout';
import AppContext from '@/contexts/appContext';
import { useNarrowNav } from '@/contexts/narrowNavContext';
import { useSidebarLayout } from '@/contexts/sidebarLayoutContext';
import AffectBadge from '@/ui/components/AffectBadge';
import { getAvailableWorkspaceNavOptions } from '@/utils/workspaceNavOptions';

import type { Props } from './types';

/** Matches the idle link treatment in SidebarNavRail; see the note there about dark mode. */
const FOOTER_ACTION =
  'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-gray-100';

/**
 * Dugout grew up as a narrow chrome-extension panel, so navigation lives in a `<select>`.
 * On wide viewports we have room for a real sidebar; this renders the workspace rail.
 */
const DesktopSidebarNav = ({ onOpenGlobalSearch, originUrl }: Props) => {
  const { pathname } = useLocation();
  const { currentUser, pendingRequests, permissions } = useContext(AppContext);
  const { railsCollapsed, toggleRailsCollapsed, railWidthClass } = useSidebarLayout();
  /*
   * The menu navigation keeps the top bar up at every width, and the bar carries search, the
   * dialer and the account menu, so the rail's footer is left with only its collapse toggle.
   */
  const { navStyle } = useNarrowNav();

  const items = useMemo(() => getAvailableWorkspaceNavOptions(permissions), [permissions]);

  // Routes are mounted under both `/` and `/agent/`, so compare without the prefix.
  const currentPath = pathname.replace(/^\/agent/, '') || '/';

  const isCurrent = useCallback(
    (path: string) => {
      const normalized = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;

      if (normalized === '/') return currentPath === '/';

      return currentPath === normalized || currentPath.startsWith(`${normalized}/`);
    },
    [currentPath]
  );

  const userName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
    : 'My Profile';

  return (
    <SidebarNavRail
      ariaLabel="Workspace"
      collapsed={railsCollapsed}
      onToggleCollapsed={toggleRailsCollapsed}
      widthClass={railWidthClass}
      leftClass={WORKSPACE_SIDEBAR_LEFT_CLASS}
      zIndexClass={WORKSPACE_SIDEBAR_Z_CLASS}
      items={items}
      isCurrent={isCurrent}
      header={
        <Link
          to="/"
          aria-label="Dugout home"
          className="flex items-center gap-x-2 text-gray-900"
          title="Dugout home">
          <AffectBadge className="h-7 w-7 shrink-0 text-green-600" />
          <span className="text-base font-semibold tracking-tightest">Dugout</span>
        </Link>
      }
      collapsedHeader={
        <Link
          to="/"
          aria-label="Dugout home"
          className="mx-auto flex items-center"
          title="Dugout home">
          <AffectBadge className="h-7 w-7 shrink-0 text-green-600" />
        </Link>
      }
      footer={
        navStyle === 'select' && (
          <>
            {/* The dropdown navigation hides the top bar on desktop, so search and the dialer live here. */}
            <div className={`flex ${railsCollapsed ? 'flex-col' : 'gap-x-1'} gap-y-1`}>
              <button
                type="button"
                onClick={onOpenGlobalSearch}
                title="Search"
                className={`flex items-center justify-center gap-x-2 rounded-md py-2 text-sm font-semibold ${FOOTER_ACTION} ${
                  railsCollapsed ? 'w-full' : 'flex-1'
                }`}>
                <FontAwesomeIcon aria-hidden="true" className="h-4 w-4" icon={faMagnifyingGlass} />
                <span className={railsCollapsed ? 'sr-only' : ''}>Search</span>
              </button>

              <DialpadButton
                label={railsCollapsed ? undefined : 'Call'}
                wrapperClassName={railsCollapsed ? 'w-full' : 'flex-1'}
                className={`flex w-full items-center justify-center gap-x-2 rounded-md py-2 text-sm font-semibold ${FOOTER_ACTION}`}
                popoverClassName="absolute bottom-full left-0 z-40 mb-2"
              />
            </div>

            <ProfileMenu
              originUrl={originUrl}
              buttonAriaLabel={`Account menu for ${userName}`}
              customWrapperClassName="relative block"
              customMenuClassName="bottom-full left-0 mb-2 origin-bottom-left"
              customButtonClassName={`flex w-full items-center rounded-md py-2 text-sm font-semibold ${FOOTER_ACTION} ${
                railsCollapsed ? 'justify-center px-2' : 'gap-x-3 px-3'
              }`}
              buttonText={
                <>
                  <FontAwesomeIcon aria-hidden="true" className="h-5 w-5 shrink-0" icon={faUser} />
                  <span className={railsCollapsed ? 'sr-only' : 'truncate'}>{userName}</span>
                  {pendingRequests.length > 0 && (
                    <FontAwesomeIcon
                      className="ml-auto h-4 w-4 animate-spin"
                      icon={faSpinner}
                      title="Pending requests"
                    />
                  )}
                </>
              }
            />
          </>
        )
      }
    />
  );
};

export default React.memo(DesktopSidebarNav);
